import React from 'react'
import { Typography, useTheme, Box } from '@mui/material'
import { grey } from '@mui/material/colors'

import { CreditCardIcon } from '../../Icons/CreditCardIcon'
import { TransitionButton } from '../../Buttons/TransitionButton'
import { PromotionDetails as ApplicationReadyDetails } from '../../MemberExclusive/PromotionDetails'
import { ApplicationReadyDisclaimer } from './ApplicationReadyDisclaimer'

import {
  ApplicationReadyComponentProps,
  CASH_PROGRAM_TERMS_URL,
  OFFER_TERMS_URL,
  PL_PRIVACY_POLICY_URL
} from './types'
import { AmfWaiver } from '../../MemberExclusive/AmfWaiver'

export const ApplicationReadyComponent: React.FC<
  ApplicationReadyComponentProps
> = ({
  creditOffer,
  riskBasedPricingDoc,
  onDeclineOffer,
  onAcceptOffer,
  loading,
  decliningOffer,
  promotionOverride
}) => {
  const theme = useTheme()
  const { agreementUrl, limit, purchase } = creditOffer || {}
  const riskBasedPricingData = riskBasedPricingDoc
    ? `data:application/pdf;base64,${riskBasedPricingDoc}`
    : null

  return (
    <Box
      sx={{
        margin: '-40px',
        mb: '-60px',
        pb: 3,
        maxWidth: 700
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          maxWidth: 700,
          borderRadius: '10px 10px 0 0'
        }}
      >
        <Box
          sx={{
            backgroundImage: `linear-gradient(to right, ${theme.palette.gradient.main}, ${theme.palette.secondary.main})`,
            pt: 3,
            pb: 3,
            minWidth: 350,
            flex: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              justifyContent: 'space-around',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h5"
              component="div"
              fontWeight={600}
              gutterBottom
            >
              Congratulations!
            </Typography>

            <Typography
              variant="body1"
              component="div"
              fontWeight={600}
              gutterBottom
            >
              You have been pre-approved.
            </Typography>

            <CreditCardIcon
              style={{
                height: '7em',
                width: '10em',
                filter: 'drop-shadow(5px 5px 5px rgba(0,0,0,0.3))',
                borderRadius: 10
              }}
            />
          </Box>

          <Box sx={{ pr: 2, pl: 2 }}>
            <ApplicationReadyDetails />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            bgcolor: 'background.paper',
            flex: 1,
            pt: 2,
            pb: 1,
            minWidth: 350,
            flexFlow: 'column',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {/* Promotion Override */}
          {promotionOverride && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="div"
                textAlign="center"
              >
                {promotionOverride.name} Exclusive
              </Typography>

              <AmfWaiver
                logoUrl={promotionOverride.assets.logoUrl}
                borderColor={promotionOverride.colors.main}
                highlighted
              />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              width: '100%',
              borderBottom: `1px solid #56C9F1`,
              pl: 1,
              pr: 1,
              mb: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '.8rem',
                borderBottom: `1px solid ${grey[200]}`,
                pb: 1
              }}
            >
              <span>Credit Limit</span>
              <span>{limit}</span>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '.8rem',
                borderBottom: `1px solid ${grey[200]}`,
                pb: 1,
                pt: 1
              }}
            >
              <span>APR</span>
              <span>{purchase}%</span>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '.8rem',
                borderBottom: `1px solid ${grey[200]}`,
                pb: 1,
                pt: 1
              }}
            >
              <span>
                Cash Back<sup>1</sup>
              </span>
              <span>Up to 5% on Wellness Spend</span>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '.8rem',
                pb: 1,
                pt: 1
              }}
            >
              <span>
                Annual Fee<sup>2</sup>
              </span>
              <span>$60</span>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              pl: 2,
              pr: 2
            }}
          >
            <Typography
              sx={{
                mb: theme.spacing(1),
                lineHeight: 1.3
              }}
              variant="caption"
              component="p"
              fontWeight={600}
            >
              View your offer, customer agreement, credit score notice, and APR
              details from Evolve Bank.
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{ mb: theme.spacing(0.5), lineHeight: 1.3 }}
            >
              <a href={agreementUrl} target="_blank">
                Paceline Consumer Credit Card Holder Agreement
              </a>
            </Typography>
            {riskBasedPricingData && (
              <Typography
                component="span"
                variant="caption"
                sx={{ mb: theme.spacing(0.5), lineHeight: 1.3 }}
              >
                <a
                  href={riskBasedPricingData}
                  download="Risk Based Pricing Notice.pdf"
                >
                  Risk Based Pricing Notice
                </a>
              </Typography>
            )}
            <Typography
              component="span"
              variant="caption"
              sx={{ mb: theme.spacing(1), lineHeight: 1.3 }}
            >
              <a href={PL_PRIVACY_POLICY_URL} target="_blank">
                Privacy Notice
              </a>
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{ mb: theme.spacing(1), lineHeight: 1.3 }}
            >
              <a href={CASH_PROGRAM_TERMS_URL} target="_blank">
                Evolve Paceline Cash Rewards Program Terms and Conditions
              </a>
            </Typography>
            <Typography
              component="span"
              variant="caption"
              sx={{ mb: theme.spacing(1), lineHeight: 1.3 }}
            >
              <a href={OFFER_TERMS_URL} target="_blank">
                Paceline Qualifying Fitness Device Purchase Reimbursement Terms
                and Conditions
              </a>
            </Typography>
            <Typography
              sx={{
                mb: theme.spacing(1)
              }}
              variant="caption"
              component="p"
            >
              By tapping or clicking "Accept Offer" button we will notify the
              credit agencies that you were approved for this credit card. A
              hard inquiry will appear on your credit report which may impact
              your credit score.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              justifyContent: 'space-around',
              width: '75%',
              mt: 1
            }}
          >
            <TransitionButton
              sx={{
                pl: 1,
                pr: 1
              }}
              size="medium"
              text="Decline Offer"
              variant="outlined"
              loading={decliningOffer}
              disabled={loading || decliningOffer}
              onClick={onDeclineOffer}
            />
            <TransitionButton
              sx={{
                pl: 1,
                pr: 1
              }}
              size="medium"
              text="Accept Offer"
              loading={loading}
              disabled={loading || decliningOffer}
              onClick={onAcceptOffer}
            />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          mt: 4,
          pl: 2,
          pr: 2
        }}
      >
        <ApplicationReadyDisclaimer />
      </Box>
    </Box>
  )
}
